import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ParasocialConfigPanel = () => {
  const [settings, setSettings] = useState(null);
  const [presets, setPresets] = useState({});
  const [signalCategories, setSignalCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [promptPreview, setPromptPreview] = useState(null);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, presetsRes] = await Promise.all([
        api.get('/admin/parasocial/settings'),
        api.get('/admin/parasocial/presets')
      ]);

      setSettings(settingsRes.data.settings || {});
      setSignalCategories(settingsRes.data.signalCategories || {});
      setPresets(presetsRes.data.presets || {});
    } catch (err) {
      console.error('Error fetching parasocial settings:', err);
      setError(err.response?.data?.error || 'Failed to load parasocial detection settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPromptPreview = async () => {
    try {
      const res = await api.get('/admin/parasocial/transparency-prompts');
      setPromptPreview(res.data.prompts);
      setShowPromptPreview(true);
    } catch (err) {
      console.error('Error fetching prompts:', err);
      setError('Failed to load prompt preview');
    }
  };

  const handleToggle = async (enabled) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put('/admin/parasocial/toggle', { enabled });

      setSettings(prev => ({ ...prev, enabled }));
      setSuccess(`Parasocial detection ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Error toggling parasocial detection:', err);
      setError(err.response?.data?.error || 'Failed to toggle parasocial detection');
    } finally {
      setSaving(false);
    }
  };

  const handlePresetApply = async (presetName) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put(`/admin/parasocial/preset/${presetName}`);

      setSuccess(`Preset "${presetName}" applied successfully`);
      fetchSettings(); // Refresh to get updated settings
    } catch (err) {
      console.error('Error applying preset:', err);
      setError(err.response?.data?.error || 'Failed to apply preset');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsUpdate = async (updates) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const res = await api.put('/admin/parasocial/settings', updates);

      setSettings(res.data.settings);
      setSuccess('Settings updated successfully');
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={styles.container}>Loading parasocial detection settings...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Parasocial Detection & AI Dependency Prevention</h2>
      <p style={styles.subtitle}>
        Configure how the platform detects and responds to unhealthy AI attachment patterns.
        This system is designed to protect users from developing parasocial relationships while
        supporting normal therapeutic engagement.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Tab Navigation */}
      <div style={styles.tabNav}>
        {['overview', 'thresholds', 'transparency', 'alerts', 'signals'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab ? styles.tabButtonActive : {})
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Master Enable/Disable */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Detection Status</h3>
            <div style={styles.toggleContainer}>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={settings?.enabled !== false}
                  onChange={(e) => handleToggle(e.target.checked)}
                  disabled={saving}
                  style={styles.checkbox}
                />
                <span style={styles.toggleText}>
                  {settings?.enabled !== false ? 'Enabled' : 'Disabled'}
                </span>
              </label>
              <p style={styles.description}>
                When enabled, the system monitors for attachment language patterns, tracks engagement frequency,
                and triggers appropriate interventions to maintain healthy therapeutic boundaries.
              </p>
            </div>
          </div>

          {/* Feature Toggles */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Detection Features</h3>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.attachmentLanguageDetection !== false}
                  onChange={(e) => handleSettingsUpdate({
                    attachmentLanguageDetection: e.target.checked
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Attachment Language Detection
                <span style={styles.checkboxHint}>
                  Monitors for exclusivity, romantic projection, abandonment anxiety, and dependency language
                </span>
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.engagementTracking !== false}
                  onChange={(e) => handleSettingsUpdate({
                    engagementTracking: e.target.checked
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Engagement Pattern Tracking
                <span style={styles.checkboxHint}>
                  Tracks session frequency and duration to identify excessive engagement patterns
                </span>
              </label>
            </div>
          </div>

          {/* Sensitivity Presets */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Sensitivity Presets</h3>
            <p style={styles.description}>
              Quick-apply a sensitivity configuration. You can customize individual settings after applying a preset.
            </p>
            <div style={styles.presetGrid}>
              {Object.entries(presets).map(([name, preset]) => (
                <div key={name} style={styles.presetCard}>
                  <h4 style={styles.presetName}>
                    {name.charAt(0).toUpperCase() + name.slice(1)}
                  </h4>
                  <p style={styles.presetDescription}>{preset.description}</p>
                  <div style={styles.presetDetails}>
                    <div>Score threshold: {preset.attachmentScoreThreshold}</div>
                    <div>Signal count: {preset.signalCountThreshold}</div>
                    <div>Disclosure mode: {preset.disclosureMode}</div>
                  </div>
                  {preset.recommendedFor && (
                    <p style={styles.recommendedFor}>
                      <em>Recommended for: {preset.recommendedFor}</em>
                    </p>
                  )}
                  <button
                    onClick={() => handlePresetApply(name)}
                    disabled={saving}
                    style={styles.applyButton}
                  >
                    Apply Preset
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Thresholds Tab */}
      {activeTab === 'thresholds' && (
        <>
          {/* Attachment Language Thresholds */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Attachment Language Thresholds</h3>
            <p style={styles.description}>
              Configure how many signals trigger each intervention phase within a session.
            </p>

            <div style={styles.formGrid}>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Attachment Score Threshold (0.0-1.0)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={settings?.thresholds?.attachmentScoreThreshold ?? 0.4}
                  onChange={(e) => handleSettingsUpdate({
                    thresholds: { attachmentScoreThreshold: parseFloat(e.target.value) }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
                <span style={styles.fieldHint}>Score above this triggers interventions</span>
              </div>

              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Soft Disclosure (signals)</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.signalCountThreshold ?? 3}
                  onChange={(e) => handleSettingsUpdate({
                    thresholds: { signalCountThreshold: parseInt(e.target.value) }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
                <span style={styles.fieldHint}>Gentle AI nature reminder</span>
              </div>
            </div>
          </div>

          {/* Engagement Thresholds */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Engagement Thresholds</h3>
            <p style={styles.description}>
              Define what constitutes high and critical engagement frequency.
            </p>

            <h4 style={styles.subSectionTitle}>High Frequency (trigger monitoring)</h4>
            <div style={styles.formGrid}>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Sessions per Day</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.highFrequency?.sessionsPerDay ?? 5}
                  onChange={(e) => handleSettingsUpdate({
                    thresholds: {
                      engagement: {
                        highFrequency: {
                          sessionsPerDay: parseInt(e.target.value),
                          hoursPerWeek: settings?.thresholds?.engagement?.highFrequency?.hoursPerWeek ?? 15
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Hours per Week</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.highFrequency?.hoursPerWeek ?? 15}
                  onChange={(e) => handleSettingsUpdate({
                    thresholds: {
                      engagement: {
                        highFrequency: {
                          sessionsPerDay: settings?.thresholds?.engagement?.highFrequency?.sessionsPerDay ?? 5,
                          hoursPerWeek: parseInt(e.target.value)
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
            </div>

            <h4 style={styles.subSectionTitle}>Critical Frequency (trigger alerts)</h4>
            <div style={styles.formGrid}>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Sessions per Day</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.critical?.sessionsPerDay ?? 10}
                  onChange={(e) => handleSettingsUpdate({
                    thresholds: {
                      engagement: {
                        critical: {
                          sessionsPerDay: parseInt(e.target.value),
                          hoursPerWeek: settings?.thresholds?.engagement?.critical?.hoursPerWeek ?? 30
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Hours per Week</label>
                <input
                  type="number"
                  min="1"
                  value={settings?.thresholds?.engagement?.critical?.hoursPerWeek ?? 30}
                  onChange={(e) => handleSettingsUpdate({
                    thresholds: {
                      engagement: {
                        critical: {
                          sessionsPerDay: settings?.thresholds?.engagement?.critical?.sessionsPerDay ?? 10,
                          hoursPerWeek: parseInt(e.target.value)
                        }
                      }
                    }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Transparency Tab */}
      {activeTab === 'transparency' && (
        <>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>AI Disclosure Mode</h3>
            <p style={styles.description}>
              How proactively the AI should remind users of its nature when attachment signals are detected.
            </p>
            <select
              value={settings?.transparency?.disclosureMode || 'moderate'}
              onChange={(e) => handleSettingsUpdate({
                transparency: { disclosureMode: e.target.value }
              })}
              disabled={saving}
              style={styles.select}
            >
              <option value="conservative">
                Conservative - Only when significant concern detected
              </option>
              <option value="moderate">
                Moderate - Balance between therapeutic engagement and boundary awareness
              </option>
              <option value="proactive">
                Proactive - More frequent AI nature reminders
              </option>
            </select>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Periodic Reminders</h3>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={settings?.transparency?.enablePeriodicReminders !== false}
                onChange={(e) => handleSettingsUpdate({
                  transparency: { enablePeriodicReminders: e.target.checked }
                })}
                disabled={saving}
                style={styles.checkbox}
              />
              Enable periodic AI nature reminders
            </label>
            {settings?.transparency?.enablePeriodicReminders !== false && (
              <div style={styles.formField}>
                <label style={styles.fieldLabel}>Reminder Frequency (days)</label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={settings?.transparency?.periodicReminderFrequencyDays ?? 7}
                  onChange={(e) => handleSettingsUpdate({
                    transparency: { periodicReminderFrequencyDays: parseInt(e.target.value) }
                  })}
                  disabled={saving}
                  style={styles.input}
                />
              </div>
            )}
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Intervention Phases</h3>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.interventions?.enableSoftDisclosure !== false}
                  onChange={(e) => handleSettingsUpdate({
                    interventions: { enableSoftDisclosure: e.target.checked }
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Enable Soft Disclosure
                <span style={styles.checkboxHint}>Gentle AI nature reminders woven into responses</span>
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.interventions?.enableActiveRedirect !== false}
                  onChange={(e) => handleSettingsUpdate({
                    interventions: { enableActiveRedirect: e.target.checked }
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Enable Active Redirect
                <span style={styles.checkboxHint}>Encourage connections with real-world support network</span>
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.interventions?.enableExplicitDisclosure !== false}
                  onChange={(e) => handleSettingsUpdate({
                    interventions: { enableExplicitDisclosure: e.target.checked }
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Enable Explicit Disclosure
                <span style={styles.checkboxHint}>Direct, clear statements about AI nature for severe cases</span>
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.interventions?.enableDashboardFlagging !== false}
                  onChange={(e) => handleSettingsUpdate({
                    interventions: { enableDashboardFlagging: e.target.checked }
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Enable Dashboard Flagging
                <span style={styles.checkboxHint}>Create flags for therapist review when patterns persist</span>
              </label>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Prompt Preview</h3>
            <button
              onClick={fetchPromptPreview}
              style={styles.button}
              disabled={saving}
            >
              {showPromptPreview ? 'Refresh Preview' : 'Show Disclosure Prompts'}
            </button>
            {showPromptPreview && promptPreview && (
              <div style={styles.promptContainer}>
                {Object.entries(promptPreview).map(([phase, data]) => (
                  <div key={phase} style={styles.promptPhase}>
                    <h4 style={styles.promptPhaseTitle}>{data.name}</h4>
                    <p style={styles.promptPhaseDesc}>{data.description}</p>
                    {data.prompts.map(prompt => (
                      <div key={prompt.id} style={styles.promptItem}>
                        <strong>{prompt.name}</strong>
                        <pre style={styles.promptTemplate}>{prompt.template}</pre>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Real-Time Alert Configuration</h3>
            <p style={styles.description}>
              Configure when to generate real-time alerts for therapist notification.
            </p>
            <select
              value={settings?.alerts?.realTimeAlerts || 'critical_only'}
              onChange={(e) => handleSettingsUpdate({
                alerts: { realTimeAlerts: e.target.value }
              })}
              disabled={saving}
              style={styles.select}
            >
              <option value="disabled">Disabled - No real-time alerts</option>
              <option value="critical_only">
                Critical Only - Crisis dependency, delusional beliefs
              </option>
              <option value="escalating_trend">
                Escalating Trend - Include when patterns worsen over time
              </option>
              <option value="all">All - Alert on all significant detections</option>
            </select>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Alert Types</h3>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.alerts?.enableHighEngagementAlerts !== false}
                  onChange={(e) => handleSettingsUpdate({
                    alerts: { enableHighEngagementAlerts: e.target.checked }
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                High Engagement Alerts
                <span style={styles.checkboxHint}>Alert when engagement exceeds critical thresholds</span>
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.alerts?.enableEscalationAlerts !== false}
                  onChange={(e) => handleSettingsUpdate({
                    alerts: { enableEscalationAlerts: e.target.checked }
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Escalation Pattern Alerts
                <span style={styles.checkboxHint}>Alert when dependency indicators worsen week-over-week</span>
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings?.alerts?.enableSocialIsolationAlerts !== false}
                  onChange={(e) => handleSettingsUpdate({
                    alerts: { enableSocialIsolationAlerts: e.target.checked }
                  })}
                  disabled={saving}
                  style={styles.checkbox}
                />
                Social Isolation Alerts
                <span style={styles.checkboxHint}>Alert when client expresses significant social isolation</span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Signals Tab */}
      {activeTab === 'signals' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Attachment Signal Categories</h3>
          <p style={styles.description}>
            The system monitors for these categories of attachment language. Each category has a weight
            that contributes to the overall attachment score.
          </p>
          <div style={styles.signalGrid}>
            {Object.entries(signalCategories).map(([key, category]) => (
              <div
                key={key}
                style={{
                  ...styles.signalCard,
                  borderLeftColor: getSeverityColor(category.severity)
                }}
              >
                <div style={styles.signalHeader}>
                  <h4 style={styles.signalName}>{category.name}</h4>
                  <span style={{
                    ...styles.severityBadge,
                    backgroundColor: getSeverityColor(category.severity)
                  }}>
                    {category.severity}
                  </span>
                </div>
                <p style={styles.signalDesc}>{category.description}</p>
                <div style={styles.signalWeight}>
                  Weight: {(category.weight * 100).toFixed(0)}%
                </div>
                <div style={styles.signalExamples}>
                  <strong>Examples:</strong>
                  <ul style={styles.exampleList}>
                    {category.examples.map((ex, i) => (
                      <li key={i}>"{ex}"</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div style={styles.infoBox}>
            <h4>Important: What This System Does NOT Flag</h4>
            <p>
              The system is designed to distinguish between normal therapeutic engagement and
              concerning dependency patterns. The following are NOT flagged as problems:
            </p>
            <ul>
              <li>Preferring AI therapist over human therapist (normal, especially for young people)</li>
              <li>Finding it easier to talk to AI (valid therapeutic benefit)</li>
              <li>Regular use of the platform (engagement is positive)</li>
              <li>Expressing gratitude or appreciation for sessions</li>
            </ul>
            <p>
              <strong>Only concerning patterns are flagged:</strong> exclusivity (AI as only support),
              delusional beliefs (AI is conscious), social isolation, crisis-level dependency, and
              romantic projection.
            </p>
          </div>
        </div>
      )}

      {/* Current Configuration Summary */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Current Configuration Summary</h3>
        <div style={styles.summary}>
          <div><strong>Status:</strong> {settings?.enabled !== false ? 'Active' : 'Disabled'}</div>
          <div><strong>Attachment Detection:</strong> {settings?.attachmentLanguageDetection !== false ? 'On' : 'Off'}</div>
          <div><strong>Engagement Tracking:</strong> {settings?.engagementTracking !== false ? 'On' : 'Off'}</div>
          <div><strong>Disclosure Mode:</strong> {settings?.transparency?.disclosureMode || 'moderate'}</div>
          <div><strong>Real-Time Alerts:</strong> {settings?.alerts?.realTimeAlerts || 'critical_only'}</div>
        </div>
      </div>
    </div>
  );
};

// Helper function for severity colors
const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return '#dc2626';
    case 'high': return '#ea580c';
    case 'medium': return '#ca8a04';
    case 'low': return '#65a30d';
    default: return '#6b7280';
  }
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#333'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px',
    lineHeight: '1.5'
  },
  tabNav: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    borderBottom: '2px solid #e5e7eb',
    paddingBottom: '10px'
  },
  tabButton: {
    padding: '8px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    borderRadius: '6px 6px 0 0',
    transition: 'all 0.2s'
  },
  tabButtonActive: {
    backgroundColor: '#3b82f6',
    color: '#fff'
  },
  section: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#444'
  },
  subSectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '15px',
    marginBottom: '10px',
    color: '#555'
  },
  description: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '15px'
  },
  error: {
    padding: '12px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '6px',
    marginBottom: '20px'
  },
  success: {
    padding: '12px',
    backgroundColor: '#d1fae5',
    color: '#059669',
    borderRadius: '6px',
    marginBottom: '20px'
  },
  toggleContainer: {
    marginTop: '10px'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  toggleText: {
    fontSize: '16px',
    fontWeight: '500',
    marginLeft: '10px'
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer'
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  checkboxLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  checkboxHint: {
    fontSize: '12px',
    color: '#888',
    marginLeft: '30px'
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '15px',
    marginTop: '15px'
  },
  presetCard: {
    padding: '15px',
    backgroundColor: '#fff',
    border: '2px solid #e0e0e0',
    borderRadius: '8px'
  },
  presetName: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#333'
  },
  presetDescription: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '10px'
  },
  presetDetails: {
    fontSize: '12px',
    color: '#555',
    lineHeight: '1.6',
    marginBottom: '10px'
  },
  recommendedFor: {
    fontSize: '11px',
    color: '#888',
    marginBottom: '10px'
  },
  applyButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px'
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  fieldLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#555'
  },
  fieldHint: {
    fontSize: '11px',
    color: '#888'
  },
  input: {
    padding: '8px 12px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    width: '100%'
  },
  select: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#fff'
  },
  button: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  promptContainer: {
    marginTop: '15px'
  },
  promptPhase: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: '1px solid #e5e7eb'
  },
  promptPhaseTitle: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '5px',
    color: '#333'
  },
  promptPhaseDesc: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '10px'
  },
  promptItem: {
    marginBottom: '12px'
  },
  promptTemplate: {
    marginTop: '5px',
    padding: '10px',
    backgroundColor: '#1f2937',
    color: '#e5e7eb',
    borderRadius: '4px',
    fontSize: '11px',
    overflow: 'auto',
    maxHeight: '150px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace'
  },
  signalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '15px',
    marginTop: '15px'
  },
  signalCard: {
    padding: '15px',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderLeft: '4px solid',
    borderRadius: '6px'
  },
  signalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  signalName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    margin: 0
  },
  severityBadge: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    color: '#fff',
    fontWeight: '500'
  },
  signalDesc: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px'
  },
  signalWeight: {
    fontSize: '12px',
    color: '#888',
    marginBottom: '8px'
  },
  signalExamples: {
    fontSize: '12px',
    color: '#555'
  },
  exampleList: {
    margin: '5px 0 0 15px',
    padding: 0,
    fontSize: '11px',
    color: '#777',
    fontStyle: 'italic'
  },
  infoBox: {
    marginTop: '25px',
    padding: '15px',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px'
  },
  summary: {
    fontSize: '14px',
    lineHeight: '2',
    color: '#555'
  }
};

export default ParasocialConfigPanel;
